<script lang="ts" module>
	import { message, type Language } from '../i18n/messages';
	import type { Printer } from '../till/printers.svelte';
	import type { SharingAnswer } from '../till/shell';

	/** What a printer prints, in words: T25's list and the onboarding's printers step (T27) say it alike. */
	export function printsWhat(language: Language, printer: Printer): string {
		const receipts = printer.purposes.includes('receipts');
		const kitchen = printer.purposes.includes('kitchen');
		if (receipts && kitchen) return message(language, 'printerPrintsAll');
		if (receipts) return message(language, 'printerPrintsReceipts');
		if (kitchen) return message(language, 'printerPrintsKitchen');
		return '';
	}

	/**
	 * Which line carries Turn on sharing (#667), for a manager only: the
	 * setup-sharing Notice when it says sharing needs the firewall step, else
	 * the shared printer's status line when it does. One press adds both
	 * rules, so the panel never shows the action twice.
	 */
	export function sharingActionOn(
		isManager: boolean,
		setupState: string | null,
		printerState: string | null,
	): 'setup' | 'printer' | null {
		if (!isManager) return null;
		if (setupState === 'needs_firewall') return 'setup';
		if (printerState === 'needs_firewall') return 'printer';
		return null;
	}

	/**
	 * What the panel shows for the shell's word: the allowed line, or a flag
	 * Band for a no or a failure. A word it does not know reads as failed; a
	 * run already waiting shows nothing new.
	 */
	export function sharingOutcome(
		answer: SharingAnswer | string,
	): 'allowed' | 'declined' | 'failed' | null {
		if (answer === 'done') return 'allowed';
		if (answer === 'declined') return 'declined';
		if (answer === 'busy') return null;
		return 'failed';
	}

	/**
	 * How long the allowed line waits for sharing to start. The store reads
	 * the rules again every minute; past this the rules it reads are not the
	 * documented ones, as when a rule of that name has another shape, so the
	 * panel says the failure, which points to the guide, and offers the
	 * action again.
	 */
	export const ALLOWED_WAIT = 90_000;
</script>

<script lang="ts">
	// T25: Printers panel. Replaces the Sell body, Back at the inline start.
	//
	// Shows the shop's printers, what each prints, its paper width, and whether
	// the cash drawer is connected to it. When a printer's purpose has failed
	// jobs, a "Not answering" chip appears.
	//
	// A manager can Add a printer, Change an existing one, or Set up again.
	// A non-manager sees only the list and no action buttons.
	//
	// A till's own setup (#522): the store's mesh says whether the till uses
	// the shop's list from Bosla ("shop") or its own setup ("till"). On the
	// shop's list, when the app lists local_setup, a manager sees a standing
	// Notice whose action turns the till's own setup on, after a confirm in
	// place, as Orders confirms a no-sale open. On its own setup a muted line
	// under the title says so (the Till page draws no line there; composed
	// from the tokens, in the brief's delta). An older store has no mesh (setup null) and
	// the panel is as before; a setup value the screen does not know shows the
	// list only.
	//
	// Setup sharing (#517): on its own setup, when the app lists share_setup,
	// a status line under the printers says the setup is shared with the
	// shop's tills and where it was made, and when; a standing flag Notice
	// says when a newer setup waits on a printer this till cannot reach; on
	// Windows without the administrator's firewall rule the line says sharing
	// needs it. Off, or a value the screen does not know, shows nothing.
	//
	// Turn on sharing (#667): for a manager, the first line that says sharing
	// needs the firewall step carries the action (sharingActionOn). The shell
	// asks Windows' administrator once and adds both rules; its answer shows
	// in place on the line pressed: the allowed line, read again every few
	// seconds until sharing starts, within ALLOWED_WAIT, or a flag Band, with
	// the action kept.
	//
	// On a Windows till whose print host is starting (#524), one status line
	// under the title says so, for everyone; it goes when the print host is
	// ready. A printHost value the screen does not know shows nothing.
	//
	// A printer plugged into the till (#523, #516, #622), on Windows or on
	// Android, shown only when the app lists windows_usb, android_usb or
	// raw_export: it says so under its name, "USB printer on this till: {name}"
	// ({name} the Windows queue's name or the Android printer's model; the line
	// with no name when it has none), where another shows nothing of its
	// place. Where raw_export is listed, a manager picks the one plugged-in
	// printer the till shares with the shop's other tills in a Choice under
	// the list (picking another moves the sharing; unpicking stops it), with
	// one status line, a Notice, and one help line. A share state the screen
	// does not know is taken as off.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Chip from '../components/Chip.svelte';
	import Notice from '../components/Notice.svelte';
	import Row from '../components/Row.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import Choice from '../components/Choice.svelte';
	import { formatWhen } from '../format/time';
	import { fill } from '../i18n/messages';
	import { turnOnSharing } from '../till/shell';
	import {
		findsAttached,
		loadMesh,
		meshAttachedName,
		meshPrintHost,
		meshShared,
		shareLocalPrinter,
		type MeshSharing,
		type Refusal,
		type SaveOutcome,
	} from '../till/printers.svelte';

	interface Props {
		language: Language;
		printers: Printer[];
		isManager: boolean;
		notAnswering: ('receipts' | 'kitchen')[];
		/** The mesh's setup: "shop", "till", a later value, or null on an older store. */
		setup?: string | null;
		/** What the app can do, such as local_setup and network. */
		capabilities?: string[];
		/** How the till shares its own setup with the shop's tills (#517); null when it shares nothing. */
		sharing?: MeshSharing | null;
		onActivate?: () => Promise<SaveOutcome>;
		/** The ids of the printers that need identifying (find_again). */
		identifying?: string[];
		onIdentify?: (printer: Printer) => void;
		onAdd: () => void;
		onChange: (printer: Printer) => void;
		onSetUpAgain: (printer: Printer) => void;
		onBack: () => void;
	}

	let {
		language,
		printers,
		isManager,
		notAnswering,
		setup = null,
		capabilities = [],
		sharing = null,
		onActivate,
		identifying = [],
		onIdentify,
		onAdd,
		onChange,
		onSetUpAgain,
		onBack,
	}: Props = $props();

	const known = $derived(setup === null || setup === 'shop' || setup === 'till');
	const manages = $derived(isManager && known);
	// On its own setup a printer is found at its address by the network transport.
	const finds = $derived(setup !== 'till' || capabilities.includes('network'));
	const offersLocal = $derived(
		isManager && setup === 'shop' && capabilities.includes('local_setup') && !!onActivate,
	);

	// Finding a printer again shows only when the app lists it, on the till's own setup.
	const findsAgain = $derived(setup === 'till' && capabilities.includes('find_again'));

	// Shared only on the till's own setup, with the capability listed.
	const share = $derived(
		setup === 'till' && capabilities.includes('share_setup') && sharing !== null ? sharing : null,
	);
	const sharedFrom = $derived.by(() => {
		if (share?.state !== 'on') return '';
		const key =
			share.source === 'another_till'
				? 'printersSharedFromAnother'
				: share.source === 'this_till'
					? 'printersSharedFromHere'
					: null;
		if (key === null) return '';
		const when = formatWhen(share.changedAt, language, {
			today: message(language, 'whenToday'),
			yesterday: message(language, 'whenYesterday'),
		});
		return fill(message(language, key), when);
	});

	// The printers plugged into the till and the one it shares (#523, #622).
	const usbShown = $derived(findsAttached(capabilities) || capabilities.includes('raw_export'));
	const usbPrinters = $derived(
		usbShown ? printers.filter((p) => meshAttachedName(p.id) !== null) : [],
	);

	function usbLine(name: string): string {
		return name
			? fill(message(language, 'printerUsbOnThisTill'), name)
			: message(language, 'printerUsbOptionAnon');
	}
	const sharesUsb = $derived(
		isManager && setup === 'till' && capabilities.includes('raw_export') && usbPrinters.length > 0,
	);
	const shared = $derived(sharesUsb ? meshShared() : null);
	const sharedId = $derived(shared?.sinkId ?? '');
	let sharingBusy = $state(false);

	// The shared printer's state follows the printer and the firewall: read
	// it again while the list shows it.
	$effect(() => {
		if (!sharesUsb) return;
		const timer = setInterval(() => void loadMesh(), 5000);
		return () => clearInterval(timer);
	});

	async function pickShared(picked: string[]) {
		if (sharingBusy) return;
		// The option just picked moves the sharing to it; unpicking stops it.
		const next = picked.find((id) => id !== sharedId) ?? '';
		sharingBusy = true;
		refusal = null;
		const outcome = await shareLocalPrinter(next);
		sharingBusy = false;
		if (outcome.kind === 'refused') refusal = outcome;
	}

	const shareOptions = $derived(
		usbPrinters.map((p) => ({
			value: p.id,
			label: p.name,
			sub: usbLine(meshAttachedName(p.id) ?? ''),
			testId: `printer-share-${p.id}`,
		})),
	);

	const shareLine = $derived.by(() => {
		if (!shared || sharedId === '') return null;
		switch (shared.state) {
			case 'on':
				return {
					tone: 'neutral' as const,
					text: fill(message(language, 'printerSharedAt'), shared.addresses.join(', ')),
				};
			case 'needs_firewall':
				return { tone: 'flag' as const, text: message(language, 'printerShareFirewall') };
			case 'port_taken':
				return { tone: 'flag' as const, text: message(language, 'printerSharePortTaken') };
			case 'printer_offline':
				return { tone: 'flag' as const, text: message(language, 'printerShareOffline') };
		}
		return null;
	});

	// Turn on sharing (#667).
	const sharingAction = $derived(
		sharingActionOn(
			isManager,
			share?.state ?? null,
			shared && sharedId !== '' ? shared.state : null,
		),
	);
	let turning = $state(false);
	// The line whose press Windows allowed, and the line whose press it
	// refused, with why.
	let allowedOn = $state<'setup' | 'printer' | null>(null);
	let sharingRefused = $state<{ line: 'setup' | 'printer'; why: 'declined' | 'failed' } | null>(
		null,
	);
	const allowed = $derived(allowedOn !== null && allowedOn === sharingAction);

	async function turnOn() {
		const line = sharingAction;
		if (turning || line === null) return;
		turning = true;
		sharingRefused = null;
		const outcome = sharingOutcome(await turnOnSharing());
		turning = false;
		if (outcome === 'allowed') allowedOn = line;
		else if (outcome !== null) sharingRefused = { line, why: outcome };
	}

	// Once Windows allowed it, read the mesh again for ALLOWED_WAIT; the
	// shared printer's line is read every 5 seconds already. Past it, a line
	// that still needs the firewall step says the failure and offers the
	// action again. The wait hangs on allowedOn alone, so a moment with no
	// action during a mesh read neither stops the reads nor starts it again.
	$effect(() => {
		if (allowedOn === null) return;
		const line = allowedOn;
		const timer = line === 'setup' ? setInterval(() => void loadMesh(), 5000) : undefined;
		const late = setTimeout(() => {
			allowedOn = null;
			if (sharingAction === line) sharingRefused = { line, why: 'failed' };
		}, ALLOWED_WAIT);
		return () => {
			clearInterval(timer);
			clearTimeout(late);
		};
	});

	const sharingRefusalText = $derived(
		sharingRefused?.why === 'declined'
			? message(language, 'printersShareDeclined')
			: message(language, 'printersShareFailed'),
	);
	const refusedOn = $derived(
		sharingRefused !== null && sharingRefused.line === sharingAction ? sharingAction : null,
	);

	let asking = $state(false);
	let activating = $state(false);
	let refusal = $state<Refusal | null>(null);

	// While a Windows till's print host is starting (#524), the panel reads
	// the mesh again every few seconds, so its line goes once it is ready.
	const hostCheck = 3000;
	$effect(() => {
		if (meshPrintHost() !== 'starting') return;
		const timer = setInterval(() => void loadMesh(), hostCheck);
		return () => clearInterval(timer);
	});

	async function activate() {
		if (activating || !onActivate) return;
		activating = true;
		refusal = null;
		const outcome = await onActivate();
		activating = false;
		asking = false;
		if (outcome.kind === 'refused') refusal = outcome;
	}

	const refusalText = $derived.by(() => {
		if (!refusal) return '';
		if (refusal.code === 'print_in_progress') return message(language, 'printersPrintInProgress');
		if (refusal.code === 'forbidden') return message(language, 'printerForbidden');
		return message(language, 'printerFailed');
	});

	function needsIdentifying(printer: Printer): boolean {
		return findsAgain && identifying.includes(printer.id);
	}

	function isNotAnswering(printer: Printer): boolean {
		return printer.purposes.some((p) => notAnswering.includes(p));
	}
</script>

<div class="printers" data-testid="panel-printers" data-scroll="">
	<div class="bar">
		<Button
			testId="printers-back"
			icon="back"
			mirrorIcon={language === 'en'}
			label={message(language, 'printersBack')}
			tone="secondary"
			size="sm"
			onPress={onBack}
		/>
		<h2>{message(language, 'printersTitle')}</h2>
		<span class="grow"></span>
		{#if manages && finds && printers.length > 0}
			<Button
				testId="printers-add"
				icon="plus"
				label={message(language, 'printersAdd')}
				tone="primary"
				size="sm"
				onPress={onAdd}
			/>
		{/if}
	</div>

	{#if setup === 'till'}
		<p class="on-till" data-testid="printers-on-till">{message(language, 'printersOnThisTill')}</p>
	{/if}

	{#if meshPrintHost() === 'starting'}
		<p class="on-till" data-testid="printers-host-starting">
			{message(language, 'printersHostStarting')}
		</p>
	{/if}

	{#if share?.state === 'on' && share.waiting}
		<Notice life="standing" tone="flag" testId="printers-share-waiting">
			{message(language, 'printersShareWaiting')}
		</Notice>
	{/if}

	{#if offersLocal}
		{#if asking}
			<Notice life="standing" tone="neutral" testId="printers-local">
				<span class="ask">
					<span data-testid="printers-local-ask">{message(language, 'printersSetUpHereAsk')}</span>
					<span class="ask-actions">
						<Button
							testId="printers-local-yes"
							label={message(language, 'printerYes')}
							size="sm"
							state={activating ? 'pending' : 'idle'}
							pendingLabel={message(language, 'saving')}
							onPress={() => void activate()}
						/>
						<Button
							testId="printers-local-no"
							label={message(language, 'printerNo')}
							tone="text-quiet"
							size="sm"
							disabled={activating}
							onPress={() => (asking = false)}
						/>
					</span>
				</span>
			</Notice>
		{:else}
			<Notice
				life="standing"
				tone="neutral"
				testId="printers-local"
				action={message(language, 'printersSetUpHere')}
				actionTestId="printers-local-setup"
				onAction={() => {
					refusal = null;
					asking = true;
				}}
			>
				{message(language, 'printersFromShop')}
			</Notice>
		{/if}
	{/if}

	{#if refusal}
		<Band tone="flag" alert testId="printers-local-error">{refusalText}</Band>
	{/if}

	{#if printers.length === 0}
		<div class="empty-wrap" data-testid="printers-empty">
			<div class="empty-box">
				<p class="empty-strong">{message(language, 'printersEmpty')}</p>
				{#if !isManager}
					<p class="empty-quiet">{message(language, 'printersManagerSetsUp')}</p>
				{:else if manages && finds}
					<Button
						testId="printers-add"
						icon="plus"
						label={message(language, 'printersAdd')}
						tone="primary"
						size="md"
						onPress={onAdd}
					/>
				{/if}
			</div>
		</div>
	{:else}
		<TablePanel
			columns={[
				{ label: message(language, 'printerNameLabel'), w: 'minmax(0, 1fr)' },
				{ label: message(language, 'printerPrintsLabel'), w: 'minmax(0, 1.3fr)' },
				{ label: message(language, 'printerPaperLabel'), w: '80px' },
				{ label: message(language, 'printerDrawer'), w: '110px' },
				...(manages ? [{ label: '', w: 'minmax(0, 1fr)' }] : []),
			]}
			testId="printers-list"
		>
			{#each printers as printer (printer.id)}
				<Row testId="printer-row-{printer.id}">
					<span class="name-cell">
						<span class="name">{printer.name}</span>
						{#if usbShown && meshAttachedName(printer.id) !== null}
							<span class="soft" data-testid="printer-usb-{printer.id}"
								>{usbLine(meshAttachedName(printer.id) ?? '')}</span
							>
						{/if}
						{#if needsIdentifying(printer)}
							<Chip
								dot
								size="md"
								tone="flag"
								text={message(language, 'printerNeedsIdentifying')}
								testId="printer-needs-identifying-{printer.id}"
							/>
							<span class="soft note" data-testid="printer-identifying-note-{printer.id}">
								{message(language, 'printerNeedsIdentifyingNote')}
							</span>
						{:else if isNotAnswering(printer)}
							<Chip
								dot
								size="md"
								tone="flag"
								text={message(language, 'printerNotAnswering')}
								testId="printer-not-answering-{printer.id}"
							/>
						{/if}
						{#if findsAgain && printer.address}
							<span class="soft note" data-testid="printer-address-{printer.id}">
								{message(language, 'printerAtAddress').split('%1')[0]}<bdi dir="ltr" class="mono"
									>{printer.address}</bdi
								>{message(language, 'printerAtAddress').split('%1')[1] ?? ''}
							</span>
						{/if}
					</span>
					<span class="soft">{printsWhat(language, printer)}</span>
					<span class="soft"
						><bdi dir="ltr" class="mono">{printer.paperWidthMm}</bdi>
						{message(language, 'printerPaperUnit')}</span
					>
					<span class="soft">
						{#if printer.purposes.includes('receipts')}
							{message(language, printer.hasDrawer ? 'printerYes' : 'printerNo')}
						{/if}
					</span>
					{#if manages}
						<span class="actions">
							{#if needsIdentifying(printer) && onIdentify}
								<Button
									testId="printer-identify-{printer.id}"
									label={message(language, 'printersIdentify')}
									tone="primary"
									size="sm"
									onPress={() => onIdentify(printer)}
								/>
							{/if}
							{#if finds}
								<Button
									testId="printer-again-{printer.id}"
									label={message(language, 'printersSetUpAgain')}
									tone="text"
									size="sm"
									onPress={() => onSetUpAgain(printer)}
								/>
							{/if}
							<Button
								testId="printer-change-{printer.id}"
								icon="edit"
								label={message(language, 'printersChange')}
								tone="secondary"
								size="sm"
								onPress={() => onChange(printer)}
							/>
						</span>
					{/if}
				</Row>
			{/each}
		</TablePanel>
	{/if}

	{#if share?.state === 'on' && sharedFrom}
		<Notice life="standing" tone="neutral" testId="printers-share">
			{message(language, 'printersShared')} · {sharedFrom}
		</Notice>
	{:else if share?.state === 'needs_firewall'}
		<Notice
			life="standing"
			tone="neutral"
			testId="printers-share-firewall"
			action={sharingAction === 'setup' && !allowed
				? message(language, 'printersShareTurnOn')
				: undefined}
			actionTestId="printers-share-turn-on"
			actionBusy={turning}
			onAction={() => void turnOn()}
		>
			{message(
				language,
				sharingAction === 'setup' && allowed
					? 'printersShareAllowed'
					: 'printersShareNeedsFirewall',
			)}
		</Notice>
		{#if refusedOn === 'setup'}
			<Band tone="flag" alert testId="printers-share-error">{sharingRefusalText}</Band>
		{/if}
	{/if}

	{#if sharesUsb}
		<div class="share" data-testid="printers-usb-share">
			<Choice
				testId="printers-share-choice"
				title={message(language, 'printerShare')}
				mode="multi"
				stack
				options={shareOptions}
				value={sharedId === '' ? [] : [sharedId]}
				onChange={(val) => void pickShared(val as string[])}
			/>
			{#if shareLine}
				{@const allowedHere = sharingAction === 'printer' && allowed}
				<Notice
					life="standing"
					tone={allowedHere ? 'neutral' : shareLine.tone}
					testId="printers-share-state"
					action={sharingAction === 'printer' && !allowed
						? message(language, 'printersShareTurnOn')
						: undefined}
					actionTestId="printers-share-turn-on"
					actionBusy={turning}
					onAction={() => void turnOn()}
				>
					{allowedHere ? message(language, 'printersShareAllowed') : shareLine.text}
				</Notice>
			{/if}
			{#if refusedOn === 'printer'}
				<Band tone="flag" alert testId="printers-share-error">{sharingRefusalText}</Band>
			{/if}
			<p class="soft help" data-testid="printers-share-help">
				{message(language, 'printerShareHelp')}
			</p>
		</div>
	{/if}
</div>

<style>
	.printers {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		padding: var(--space-7);
		overflow-y: auto;
		overflow-x: hidden;
	}

	.bar {
		flex: none;
		display: flex;
		align-items: center;
		gap: var(--space-5);
		flex-wrap: wrap;
	}

	h2 {
		margin: 0;
		font-size: var(--text-h4);
		font-weight: 700;
	}

	.grow {
		flex: 1;
	}

	/* A quiet line under the title, as the panel's other quiet text. */
	.on-till {
		flex: none;
		margin: 0;
		font-size: var(--text-base);
		color: var(--text-muted);
	}

	.ask {
		display: flex;
		align-items: center;
		gap: var(--space-5);
		flex-wrap: wrap;
	}

	.ask-actions {
		display: flex;
		align-items: center;
		gap: var(--space-3);
	}

	.empty-wrap {
		flex: 1;
		display: grid;
		place-items: center;
	}

	.empty-box {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--space-5);
		text-align: center;
	}

	.empty-strong {
		margin: 0;
		font-size: var(--text-md);
		font-weight: 600;
		color: var(--text-strong);
	}

	.empty-quiet {
		margin: 0;
		font-size: var(--text-base);
		color: var(--text-body);
	}

	.name-cell {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-2);
		min-inline-size: 0;
	}

	.name {
		font-weight: 700;
		color: var(--text-strong);
	}

	.soft {
		color: var(--text-body);
	}

	.note {
		font-size: var(--text-sm);
	}

	.mono {
		font-family: var(--font-mono);
	}

	.share {
		flex: none;
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	.help {
		margin: 0;
		font-size: var(--text-base);
	}

	.actions {
		min-inline-size: 0;
		display: flex;
		gap: var(--space-3);
		justify-content: flex-end;
		flex-wrap: wrap;
	}
</style>
